import { useEffect, useState, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Upload, FileJson, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { useImportData } from '@/application/services/data-transfer.service'
import type { DataImportResult } from '@/application/services/data-transfer.service'
import { investigationKeys } from '@/application/services/investigation.service'
import { API_BASE_URL } from '@/infrastructure/api/client'
import { useAuthStore } from '@/application/stores/auth.store'
import { Button } from '@/ui/components/common/Button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/ui/components/common/Card'

interface ImportProgress {
  done: number
  total: number
}

function parseEventData<T>(payload: string): T {
  return JSON.parse(payload)
}

export function ImportSection() {
  const queryClient = useQueryClient()
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [importResult, setImportResult] = useState<DataImportResult | null>(null)
  const [importProgress, setImportProgress] = useState<ImportProgress | null>(null)
  const [isImporting, setIsImporting] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const clientIdRef = useRef(crypto.randomUUID())

  const importMutation = useImportData()

  useEffect(() => {
    const token = useAuthStore.getState().accessToken ?? ''
    const eventSource = new EventSource(
      `${API_BASE_URL}/data-imports/progress/${clientIdRef.current}?token=${encodeURIComponent(token)}`,
    )

    const handleProgress = (event: MessageEvent<string>) => {
      setImportProgress(parseEventData<ImportProgress>(event.data))
    }

    const handleComplete = (event: MessageEvent<string>) => {
      const result = parseEventData<DataImportResult>(event.data)
      setImportResult(result)
      setImportProgress(null)
      setIsImporting(false)
      setSelectedFile(null)
      toast.success('Data imported successfully')
      void queryClient.invalidateQueries({ queryKey: investigationKeys.allPrefix })
    }

    const handleFailed = (event: MessageEvent<string>) => {
      const { message } = parseEventData<{ message: string }>(event.data)
      toast.error(message || 'Failed to import data')
      setImportProgress(null)
      setIsImporting(false)
    }

    eventSource.addEventListener('progress', handleProgress)
    eventSource.addEventListener('complete', handleComplete)
    eventSource.addEventListener('failed', handleFailed)

    return () => {
      eventSource.removeEventListener('progress', handleProgress)
      eventSource.removeEventListener('complete', handleComplete)
      eventSource.removeEventListener('failed', handleFailed)
      eventSource.close()
    }
  }, [queryClient])

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0]
      if (file.type !== 'application/json' && !file.name.endsWith('.json')) {
        toast.error('Only JSON files are allowed')
        return
      }
      setSelectedFile(file)
      setImportResult(null)
    }
  }

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
  }

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      const file = event.dataTransfer.files[0]
      if (file.type !== 'application/json' && !file.name.endsWith('.json')) {
        toast.error('Only JSON files are allowed')
        return
      }
      setSelectedFile(file)
      setImportResult(null)
    }
  }

  const handleImport = () => {
    if (!selectedFile) return

    setImportProgress(null)
    setIsImporting(true)
    importMutation.mutate(
      { file: selectedFile, clientId: clientIdRef.current },
      {
        onError: (error) => {
          toast.error(error.message || 'Failed to import data')
          setIsImporting(false)
        },
      },
    )
  }

  const clearSelection = () => {
    setSelectedFile(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Upload className="w-5 h-5" />
          Import Data
        </CardTitle>
        <CardDescription>
          Upload a JSON file containing investigations, leads, and facts.
        </CardDescription>
        <div className="pt-1">
          <a
            href="/data-import.schema.json"
            download="data-import.schema.json"
            className="text-sm text-primary hover:underline flex items-center gap-1"
          >
            <FileJson className="w-4 h-4" />
            Download Schema
          </a>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {!selectedFile && !importResult && !isImporting && (
          <div
            className="border-2 border-dashed border-border rounded-xl p-8 text-center hover:bg-muted/50 transition-colors cursor-pointer group"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
              <FileJson className="w-6 h-6 text-muted-foreground" />
            </div>
            <h3 className="font-medium mb-1">Click or drag file to this area to upload</h3>
            <p className="text-sm text-muted-foreground">Support for a single JSON file upload.</p>
          </div>
        )}

        {selectedFile && !importResult && !isImporting && (
          <div className="bg-muted/40 rounded-xl p-6 border border-border/50 flex flex-col items-center justify-center">
            <FileJson className="w-12 h-12 text-primary mb-3" />
            <h3 className="font-medium text-lg">{selectedFile.name}</h3>
            <p className="text-sm text-muted-foreground mb-6">
              {(selectedFile.size / 1024).toFixed(2)} KB
            </p>

            <div className="flex gap-3">
              <Button variant="outline" onClick={clearSelection}>
                Cancel
              </Button>
              <Button onClick={handleImport} disabled={importMutation.isPending}>
                {importMutation.isPending ? 'Uploading...' : 'Start Import'}
              </Button>
            </div>
          </div>
        )}

        {isImporting && (
          <div className="bg-muted/40 rounded-xl p-6 border border-border/50 flex flex-col items-center justify-center">
            <Loader2 className="w-10 h-10 text-primary mb-3 animate-spin" />
            <h3 className="font-medium mb-1">Importing data…</h3>
            {importProgress && importProgress.total > 0 ? (
              <>
                <div className="w-full max-w-xs bg-muted rounded-full h-2 mt-2 mb-2 overflow-hidden">
                  <div
                    className="bg-primary h-2 rounded-full transition-all"
                    style={{
                      width: `${Math.min(100, (importProgress.done / importProgress.total) * 100)}%`,
                    }}
                  />
                </div>
                <p className="text-sm text-muted-foreground">
                  {importProgress.done} / {importProgress.total}
                </p>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">Starting import…</p>
            )}
          </div>
        )}

        {importResult && (
          <div className="space-y-4">
            <div className="bg-green-500/10 border border-green-500/20 text-green-600 dark:text-green-400 p-4 rounded-xl flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 mt-0.5" />
              <div>
                <h4 className="font-medium mb-1">Import Completed</h4>
                <ul className="text-sm space-y-1">
                  <li>Investigations created: {importResult.investigationsCreated}</li>
                  <li>Leads created: {importResult.leadsCreated}</li>
                  <li>Facts created: {importResult.factsCreated}</li>
                </ul>
              </div>
            </div>

            {importResult.errors.length > 0 && (
              <div className="bg-destructive/10 border border-destructive/20 text-destructive p-4 rounded-xl flex items-start gap-3">
                <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <h4 className="font-medium mb-2">Some items could not be imported:</h4>
                  <ul className="text-sm space-y-1 list-disc pl-4 opacity-90 max-h-[200px] overflow-y-auto">
                    {importResult.errors.map((errorMessage, index) => (
                      <li key={index}>{errorMessage}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            <Button variant="outline" className="w-full" onClick={() => setImportResult(null)}>
              Import Another File
            </Button>
          </div>
        )}

        <input
          type="file"
          accept=".json,application/json"
          className="hidden"
          ref={fileInputRef}
          onChange={handleFileChange}
        />
      </CardContent>
    </Card>
  )
}
